/**
 * The project chat before the rt relay carries it (docs/collaboration.md «Project chat»): a relay made up in this
 * browser, with the demo participants as the others. It keeps the history as rt would — one entry per message,
 * per person's reaction and per read mark, replayed on connect — in this browser's storage per project, and plays
 * the people: they read what you write a moment later, one of them types and answers, now and then someone
 * reacts (one reaction each, as in Instagram). Answers are canned by what the message is about; nobody pretends to understand more than that.
 */
import { CHAT_REACTIONS, isChatIncoming, type ChatConnection, type ChatIncoming, type ChatOutgoing, type ChatReaction, type ChatTransport } from './protocol';
import type { ChatTimers } from './session';
import { t, type TranslationKey } from '../../i18n/I18n';

export interface DemoChatPerson { id: string; name: string }
/** `slow` stretches every step; `offline` never connects and refuses to send: the chat's failure states on demand. */
export type DemoChatNetwork = 'normal' | 'slow' | 'offline';
export type DemoChatStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface DemoChatOptions {
  projectId: string;
  /** The people in the project now: the demo's members who are not agents and have joined. */
  people: () => readonly DemoChatPerson[];
  storage?: DemoChatStorage | null;
  now?: () => number;
  timers?: ChatTimers;
  network?: DemoChatNetwork;
  /** Someone writes on their own this long after the chat connects, once; null never. */
  ambientAfterMs?: number | null;
}

export const DEMO_CHAT_SELF = { userId: 'demo:self', get displayName() { return t('#ChatYou'); } } as const;
export const DEMO_CHAT_KEY = 'hp.chat.demo.';
/** Messages the demo keeps per project. */
const KEEP = 200;
const LATENCY: Record<DemoChatNetwork, { connect: number; send: number }> = {
  normal: { connect: 160, send: 90 }, slow: { connect: 2400, send: 1200 }, offline: { connect: 1200, send: 800 },
};
const defaultTimers: ChatTimers = { set: (run, ms) => setTimeout(run, ms), clear: handle => clearTimeout(handle as ReturnType<typeof setTimeout>) };

/** FNV-1a: the same message gets the same answer, so the demo can be checked. */
const hash = (text: string): number => { let h = 2166136261; for (const char of text) h = Math.imul(h ^ char.charCodeAt(0), 16777619); return h >>> 0; };

type Topic = { test: RegExp; answers: TranslationKey[]; emoji?: ChatReaction };
/** What the people answer, by what a message is about, in Russian or English; the first topic that matches speaks. */
// i18n-ignore-start: words matched in what a person typed
const TOPICS: Topic[] = [
  { test: /привет|здравствуй|добр(ый|ое|ого) (день|утро|вечер)|\bhi\b|hello|\bhey\b|good (morning|afternoon|evening)/i, answers: ['#ChatDemoHello1', '#ChatDemoHello2'] },
  { test: /спасибо|благодар|thank/i, answers: ['#ChatDemoThanks1', '#ChatDemoThanks2'], emoji: '❤️' },
  { test: /(ха){2,}|\){2,}|😂|lol|\b(ha){2,}/i, answers: ['#ChatDemoLaugh1', '#ChatDemoLaugh2'], emoji: '😂' },
  { test: /диван|кресл|мебел|шкаф|кроват|стол|sofa|couch|chair|furniture|wardrobe|closet|\bbeds?\b|\btables?\b|\bdesk/i, answers: ['#ChatDemoFurniture1', '#ChatDemoFurniture2'] },
  { test: /кухн|kitchen/i, answers: ['#ChatDemoKitchen1', '#ChatDemoKitchen2'] },
  { test: /цвет|покрас|обо[иея]|стен|пол\b|плитк|ламинат|паркет|colou?r|paint|wallpaper|\bwalls?\b|\bfloor|\btiles?\b|laminate|parquet/i, answers: ['#ChatDemoFinish1', '#ChatDemoFinish2'] },
  { test: /окн|двер|про[её]м|window|door|opening/i, answers: ['#ChatDemoOpening1', '#ChatDemoOpening2'] },
  { test: /3d|3д|рендер|фото|снимок|render|photo|snapshot/i, answers: ['#ChatDemoView1', '#ChatDemoView2'], emoji: '👀' },
  { test: /готово|сделан|закончен|поправлен|всё$|\bdone\b|finished|\bfixed\b|that'?s all$/i, answers: ['#ChatDemoDone1', '#ChatDemoDone2'], emoji: '🔥' },
  { test: /сделай|поправь|добавь|убери|передвинь|поставь|замени|давай|\b(make|fix|add|remove|move|put|place|replace|let'?s)\b/i, answers: ['#ChatDemoTask1', '#ChatDemoTask2'], emoji: '✅' },
  { test: /\?\s*$/, answers: ['#ChatDemoQuestion1', '#ChatDemoQuestion2', '#ChatDemoQuestion3'], emoji: '👀' },
];
// i18n-ignore-end
const FALLBACK: TranslationKey[] = ['#ChatDemoFallback1', '#ChatDemoFallback2', '#ChatDemoFallback3', '#ChatDemoFallback4'];
const AMBIENT: TranslationKey[] = ['#ChatDemoAmbient1', '#ChatDemoAmbient2'];

/** The answer to a message and a reaction to it, or nothing: the same message always gets the same. */
export function demoResponse(text: string, seed: number): { answer: string | null; emoji: ChatReaction | null } {
  const topic = TOPICS.find(candidate => candidate.test.test(text.trim()));
  const answer = topic ? topic.answers[seed % topic.answers.length] : seed % 3 === 0 ? null : FALLBACK[seed % FALLBACK.length];
  const emoji = topic?.emoji ?? (seed % 4 === 1 ? '👍' : null);
  return { answer: answer && t(answer), emoji };
}

export class DemoChatTransport implements ChatTransport {
  readonly self = { ...DEMO_CHAT_SELF };
  private log = new Map<string, ChatIncoming>();
  private receiver: ((events: ChatIncoming[]) => void) | null = null;
  private statusListener: ((value: ChatConnection) => void) | null = null;
  private handles = new Set<unknown>();
  private network: DemoChatNetwork;
  private lastAt = 0;
  private connected = false;
  private wroteThisVisit = false;
  private ambientDone = false;
  private disposed = false;
  private readonly timers: ChatTimers;

  constructor(private readonly options: DemoChatOptions) {
    this.timers = options.timers ?? defaultTimers;
    this.network = options.network ?? 'normal';
    this.load();
  }
  private now(): number { return (this.options.now ?? Date.now)(); }
  private get storageKey(): string { return DEMO_CHAT_KEY + this.options.projectId; }

  subscribe(receive: (events: ChatIncoming[]) => void, status: (value: ChatConnection) => void): () => void {
    this.receiver = receive; this.statusListener = status;
    this.connect();
    return () => { if (this.receiver === receive) { this.receiver = null; this.statusListener = null; this.connected = false; } };
  }

  /** For the dev console and the browser check: switch the made-up network while the chat is open. */
  setNetwork(network: DemoChatNetwork): void {
    this.network = network;
    if (network === 'offline' && this.connected) { this.connected = false; this.statusListener?.('disconnected'); }
    else if (network !== 'offline' && !this.connected) this.connect();
  }
  reconnect(): void { if (!this.connected) this.connect(); }

  dispose(): void {
    this.disposed = true; this.receiver = null; this.statusListener = null; this.connected = false;
    for (const handle of this.handles) this.timers.clear(handle);
    this.handles.clear();
  }

  send(events: ChatOutgoing[]): Promise<void> {
    const network = this.network;
    return new Promise((resolve, reject) => {
      this.later(LATENCY[network].send, () => {
        if (this.network === 'offline' || !this.connected) { reject(Object.assign(new Error('No connection to the chat.'), { code: 'unavailable' })); return; }
        const stamped = events.map(event => this.stamp(event, this.self));
        resolve();
        // The relay echoes everything back to its author: the confirmation of a message and its place.
        this.deliver(stamped.filter(event => event.type !== 'ChatTyping'));
        for (const event of stamped) if (event.type === 'ChatMessage' && !event.data.editedAt && !event.data.deleted) this.respond(event);
      });
    });
  }

  private connect(): void {
    this.statusListener?.('connecting');
    this.later(LATENCY[this.network].connect, () => {
      if (!this.receiver) return;
      if (this.network === 'offline') { this.statusListener?.('disconnected'); return; }
      if (!this.log.size) this.seed();
      this.connected = true;
      this.receiver([...this.log.values()]);
      this.statusListener?.('connected');
      const ambient = this.options.ambientAfterMs === undefined ? 20_000 : this.options.ambientAfterMs;
      if (ambient !== null && !this.ambientDone) this.later(ambient, () => this.ambient());
    });
  }

  private later(ms: number, run: () => void): void {
    if (this.disposed) return;
    const handle = this.timers.set(() => { this.handles.delete(handle); if (!this.disposed) run(); }, ms);
    this.handles.add(handle);
  }

  /** What the relay does to an event: stamps its author and time, keeps it by its key, a message's first time kept. */
  private stamp(event: ChatOutgoing, author: { userId: string; displayName: string }): ChatIncoming {
    // The relay's clock never repeats itself: two events in one millisecond still have an order.
    const at = this.lastAt = Math.max(this.now(), this.lastAt + 1);
    if (event.type === 'ChatMessage') {
      const key = `m:${event.data.id}`, earlier = this.log.get(key);
      const stamped = { ...event, userId: author.userId, displayName: author.displayName, at: earlier?.at ?? at } as ChatIncoming;
      // Somebody else's message id is not a way to rewrite their words: the relay keeps the author's.
      if (earlier && earlier.userId !== author.userId) return earlier;
      this.store(key, stamped);
      if (author.userId === this.self.userId) this.wroteThisVisit = true;
      return stamped;
    }
    const stamped = { ...event, userId: author.userId, displayName: author.displayName, at } as ChatIncoming;
    if (event.type === 'ChatReaction') this.store(`r:${event.key}::${author.userId}`, stamped);
    else if (event.type === 'ChatRead') this.store(`read:${author.userId}`, stamped);
    return stamped;
  }

  private store(key: string, event: ChatIncoming): void {
    this.log.set(key, event);
    this.save();
  }

  private deliver(events: ChatIncoming[]): void {
    if (events.length && this.connected) this.receiver?.(events);
  }

  /** A person does something in the chat: the event passes the relay like anyone's. */
  private act(person: DemoChatPerson, event: ChatOutgoing): void {
    if (!this.connected) return;
    this.deliver([this.stamp(event, { userId: person.id, displayName: person.name })]);
  }

  /** The people answer what you wrote: each reads it, one of them types and answers, maybe someone reacts. */
  private respond(message: Extract<ChatIncoming, { type: 'ChatMessage' }>): void {
    const people = this.options.people();
    if (!people.length) return;
    const seed = hash(message.data.id + message.data.text), mark = { messageId: message.data.id, at: message.at };
    const readAt = (index: number) => 500 + ((seed >>> (index * 3)) % 7) * 180;
    people.forEach((person, index) => this.later(readAt(index), () => this.act(person, { type: 'ChatRead', data: mark })));
    const { answer, emoji } = demoResponse(message.data.text, seed);
    // Whoever you answered answers you; otherwise one of them by the message.
    const addressed = message.data.replyTo ? this.log.get(`m:${message.data.replyTo}`)?.userId : undefined;
    const addressedIndex = people.findIndex(person => person.id === addressed);
    const responder = people[addressedIndex >= 0 ? addressedIndex : seed % people.length];
    if (answer) {
      const start = readAt(people.indexOf(responder)) + 350, typing = Math.min(4200, 900 + answer.length * 38);
      this.later(start, () => this.act(responder, { type: 'ChatTyping', data: { typing: true } }));
      if (typing > 2500) this.later(start + 2500, () => this.act(responder, { type: 'ChatTyping', data: { typing: true } }));
      this.later(start + typing, () => {
        if (!this.connected) return;
        const id = crypto.randomUUID();
        // In a crowd a direct question gets a quoted answer, so it is clear what is answered.
        const quote = people.length > 1 && /\?\s*$/.test(message.data.text) ? message.data.id : null;
        this.act(responder, { type: 'ChatMessage', key: id, data: { id, text: answer, replyTo: quote, sentAt: this.now() } });
        const reply = this.log.get(`m:${id}`);
        if (reply) people.forEach((person, index) => { if (person !== responder) this.later(700 + index * 400, () => this.act(person, { type: 'ChatRead', data: { messageId: id, at: reply.at } })); });
      });
    }
    if (emoji && CHAT_REACTIONS.includes(emoji)) {
      const reactor = people.length > 1 && answer ? people.find(person => person !== responder)! : people[(seed >>> 5) % people.length];
      this.later(readAt(people.indexOf(reactor)) + 250, () => this.act(reactor, { type: 'ChatReaction', key: message.data.id, data: { messageId: message.data.id, emoji } }));
    }
  }

  /** Once a visit, if you have not written yet, someone says something about the project. */
  private ambient(): void {
    this.ambientDone = true;
    const person = this.options.people()[0];
    if (!person || this.wroteThisVisit || !this.connected) return;
    const text = t(AMBIENT[hash(this.options.projectId) % AMBIENT.length]);
    this.act(person, { type: 'ChatTyping', data: { typing: true } });
    this.later(2200, () => {
      const id = crypto.randomUUID();
      this.act(person, { type: 'ChatMessage', key: id, data: { id, text, replyTo: null, sentAt: this.now() } });
    });
  }

  /** A first visit finds a short conversation already there: two people agreeing on the living room, a ❤️ between them. */
  private seed(): void {
    const people = this.options.people().slice(0, 2);
    if (!people.length) return;
    const now = this.now(), lines = [t('#ChatDemoSeed1'), t('#ChatDemoSeed2')];
    const messages = people.map((person, index) => {
      const at = now - (26 - index * 2) * 60_000, id = crypto.randomUUID();
      this.log.set(`m:${id}`, { type: 'ChatMessage', key: id, data: { id, text: lines[index], replyTo: null, sentAt: at }, userId: person.id, displayName: person.name, at });
      return { id, at };
    });
    const last = messages.at(-1)!;
    if (people.length > 1) {
      const [first] = people;
      this.log.set(`r:${last.id}::${first.id}`, { type: 'ChatReaction', key: last.id, data: { messageId: last.id, emoji: '❤️' }, userId: first.id, displayName: first.name, at: last.at + 60_000 });
      this.log.set(`read:${first.id}`, { type: 'ChatRead', data: { messageId: last.id, at: last.at }, userId: first.id, displayName: first.name, at: last.at + 60_000 });
    }
    this.save();
  }

  private load(): void {
    try {
      const stored = JSON.parse(this.options.storage?.getItem(this.storageKey) ?? 'null') as { v: number; events: ChatIncoming[] } | null;
      if (stored?.v !== 1 || !Array.isArray(stored.events)) return;
      // What an older version kept in another shape is left behind rather than replayed wrong.
      for (const event of stored.events.filter(isChatIncoming)) {
        const key = event.type === 'ChatMessage' ? `m:${event.data.id}` : event.type === 'ChatReaction' ? `r:${event.key}::${event.userId}` : event.type === 'ChatRead' ? `read:${event.userId}` : null;
        if (key) { this.log.set(key, event); this.lastAt = Math.max(this.lastAt, event.at); }
      }
    } catch { /* a broken record starts the chat afresh */ }
  }

  /** The history, the oldest messages beyond the demo's share dropped with their reactions, taken-off reactions forgotten. */
  private save(): void {
    if (!this.options.storage) return;
    const messages = [...this.log.values()].filter(event => event.type === 'ChatMessage');
    const dropped = new Set(messages.slice(0, Math.max(0, messages.length - KEEP)).map(event => (event as Extract<ChatIncoming, { type: 'ChatMessage' }>).data.id));
    for (const [key, event] of [...this.log]) {
      if (event.type === 'ChatMessage' && dropped.has(event.data.id)) this.log.delete(key);
      else if (event.type === 'ChatReaction' && (!event.data.emoji || dropped.has(event.data.messageId))) this.log.delete(key);
    }
    try { this.options.storage.setItem(this.storageKey, JSON.stringify({ v: 1, events: [...this.log.values()] })); }
    catch { /* a full storage keeps this visit's chat in memory */ }
  }
}
